import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { buildPaymentRoadmap, loanRequirementLabel, loanRoadmapNote, loanTriggerLabel, locateLoanTrigger, paymentRoadmapShortLabel, paymentRoadmapStripCells, percentLabel, type PaymentCondition } from '../office/paymentCondition';

type Props = { condition: PaymentCondition | null | undefined; bookingDate: string; legacyTerms?: string | null };

export default function PaymentRoadmap({ condition, bookingDate, legacyTerms }: Props) {
  const items = condition ? buildPaymentRoadmap(condition, bookingDate) : [];
  const cells = paymentRoadmapStripCells(items);
  const dpCount = cells.filter((cell) => cell.stageIndex === 0).length;
  const dpPercent = items[0]?.percent || 0;
  const loan = condition?.loan;
  const loanPosition = condition ? locateLoanTrigger(condition, items) : null;
  const loanCard = loan ? <Box sx={{ p: 1.5, bgcolor: '#f6f2eb', borderRadius: 1 }}>
    <Typography variant="body2" fontWeight={700}>Tentative loan request · {loanRequirementLabel(loan)}</Typography>
    <Typography variant="caption" display="block">{loanTriggerLabel(loan.trigger)}</Typography>
    <Typography variant="caption" color="text.secondary">This amount may include funds the customer needs for furniture. Actual loan depends on bank approval. The payment milestones still total 100%.</Typography>
  </Box> : null;
  return <Stack spacing={1.5}>
    <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, color: '#9b9b9b', textTransform: 'uppercase' }}>
      Payment milestones & loan plan
    </Typography>
    {!condition && <Alert severity="info">{legacyTerms ? `Legacy terms: ${legacyTerms}. Add structured conditions in the booking form to see the roadmap.` : 'Add conditions while creating the booking form to see the roadmap.'}</Alert>}
    {condition && cells.length > 0 && <Box sx={{ border: '1px solid #c9bba8', borderRadius: 1.5, overflow: 'hidden', color: '#2f2922' }}>
      <Stack direction="row" alignItems="center" sx={{ px: 1.5, py: 0.75, bgcolor: '#faf8f4', borderBottom: '1px solid #c9bba8' }}>
        <Typography variant="caption" fontWeight={800} sx={{ letterSpacing: '.08em' }}>PAYMENT TERMS</Typography>
      </Stack>
      <Box sx={{ overflowX: 'auto' }}>
        <Box component="table" sx={{ width: '100%', minWidth: Math.max(280, cells.length * 84), tableLayout: 'fixed', borderCollapse: 'collapse', '& th, & td': { textAlign: 'center', borderRight: '1px solid #ded6ca' }, '& th:last-child, & td:last-child': { borderRight: 0 } }}>
          <tbody>
            <tr>
              <th colSpan={dpCount} style={{ padding: '6px 4px', background: '#eee7dc', fontSize: 12, color: '#443b31' }}>DP · {percentLabel(dpPercent)}</th>
              {cells.length > dpCount && <th colSpan={cells.length - dpCount} style={{ padding: '6px 4px', background: '#f4f2ef', borderLeft: '2px solid #9d6f32', fontSize: 12, color: '#443b31' }}>REMAINING · {percentLabel(100 - dpPercent)}</th>}
            </tr>
            <tr>
              {cells.map((cell, index) => <td key={`${cell.stageIndex}-${index}`} title={cell.due} style={{ padding: '9px 3px', borderTop: '1px solid #d6cec2', borderLeft: index === dpCount ? '2px solid #9d6f32' : undefined, lineHeight: 1.2 }}>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{paymentRoadmapShortLabel(cell)}</Typography>
                <Typography variant="body2" fontWeight={800} sx={{ mt: 0.25, whiteSpace: 'nowrap' }}>{percentLabel(cell.percent)}</Typography>
              </td>)}
            </tr>
          </tbody>
        </Box>
      </Box>
      {loan && <Typography variant="caption" display="block" sx={{ px: 1.5, py: 0.75, borderTop: '1px solid #d6cec2', bgcolor: '#faf8f4' }}><strong>LOAN · {loanRequirementLabel(loan)}</strong> · {loanTriggerLabel(loan.trigger)} · {loanRoadmapNote}</Typography>}
    </Box>}
    {items.length > 0 && <Accordion disableGutters elevation={0} sx={{ border: 0, borderRadius: '0 !important', bgcolor: 'transparent', '&:before': { display: 'none' } }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: { xs: 17, sm: 19 } }} />}
        sx={{
          minHeight: { xs: 32, sm: 38 },
          px: { xs: 0.5, sm: 1 },
          '&.Mui-expanded': { minHeight: { xs: 32, sm: 38 } },
          '& .MuiAccordionSummary-content': { my: { xs: 0.5, sm: 0.75 } },
          '& .MuiAccordionSummary-content.Mui-expanded': { my: { xs: 0.5, sm: 0.75 } },
        }}
      >
        <Typography sx={{ fontSize: { xs: 11.5, sm: 12.5 }, fontWeight: 700, lineHeight: 1.25 }}>
          View milestone details and due dates
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}><Stack spacing={1}>
    {items.map((item, stageIndex) => <React.Fragment key={item.id}>{item.instalments ? <Accordion disableGutters elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ width: '100%' }}>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Typography variant="body2" fontWeight={700}>{item.label}</Typography>
            <Typography variant="body2" fontWeight={700}>{percentLabel(item.percent)}</Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">{item.due} · Expand for monthly schedule{loanPosition?.stage === stageIndex && loanPosition.row >= 0 ? ' and loan trigger' : ''}</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {item.instalments.map((row, rowIndex) => <React.Fragment key={`${row.label}-${row.due}`}><Stack direction="row" justifyContent="space-between" sx={{ py: 0.65, borderTop: '1px solid #eee' }}>
          <Typography variant="body2">{row.label} <Typography component="span" variant="caption" color="text.secondary">· {row.due}</Typography></Typography>
          <Typography variant="body2" fontWeight={600}>{percentLabel(row.percent)}</Typography>
        </Stack>{loanPosition?.stage === stageIndex && loanPosition.row === rowIndex && loanCard}</React.Fragment>)}
      </AccordionDetails>
    </Accordion> : <Box sx={{ p: 1.5, border: '1px solid #e5e7eb', borderRadius: 1 }}>
      <Stack direction="row" justifyContent="space-between"><Typography variant="body2" fontWeight={700}>{item.label}</Typography><Typography variant="body2" fontWeight={700}>{percentLabel(item.percent)}</Typography></Stack>
      <Typography variant="caption" color="text.secondary">{item.due}</Typography>
    </Box>}{loanPosition?.stage === stageIndex && loanPosition.row === -1 && loanCard}</React.Fragment>)}
    {loan && !loanPosition && loanCard}
      </Stack></AccordionDetails>
    </Accordion>}
  </Stack>;
}
